import { NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import {
  Action,
  ActionPayload,
  ActionStatus,
  ItemDecision,
} from '@maximizer/actions/domain';
import { ACTION_TRIGGER_ICON, actionTypeMeta } from './action-card-type-meta';
import { actionCardBody } from './bodies/action-card-bodies';
import { ACTION_CARD_CONTEXT } from './bodies/action-card-context';
import {
  ActionCardContext,
  ApproveEvent,
  RejectEvent,
} from '../models/action-card.model';

type FooterMode = 'decision' | 'rejecting' | 'executing' | 'settled';

type DecidableItem = { readonly decision?: ItemDecision };

/** What the API stores when a run was never told which client it was about. */
const UNKNOWN_CLIENT_ID = 'unknown';

@Component({
  selector: 'maximizer-action-card',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslatePipe,
    KENDO_BUTTON,
    KENDO_INPUTS,
    UI_KENDO,
    UI_PIPES,
    NgComponentOutlet,
  ],
  templateUrl: './action-card.component.html',
  providers: [
    {
      provide: ACTION_CARD_CONTEXT,
      useFactory: (): ActionCardContext => {
        const card = inject(ActionCardComponent);
        return {
          patchPayload: (payload) => card.onPayloadChange(payload),
          openFile: (url) => card.openFile.emit(url),
        };
      },
    },
  ],
})
export class ActionCardComponent {
  readonly action = input.required<Action>();
  readonly expanded = input(false);
  readonly payloadError = input<string | null>(null);

  readonly expandedChange = output<boolean>();
  readonly approve = output<ApproveEvent>();
  readonly reject = output<RejectEvent>();

  readonly openFile = output<string>();
  readonly retry = output<string>();

  // Seeded from the input so the inbox accordion can close a card the user
  // opened here, and reset by it on every change.
  private readonly localExpanded = linkedSignal(() => this.expanded());
  protected readonly isExpanded = this.localExpanded.asReadonly();

  private readonly draft = linkedSignal<Action, ActionPayload | null>({
    source: this.action,
    computation: () => null,
  });
  protected readonly rejectReason = signal('');
  private readonly rejecting = signal(false);

  protected readonly Status = ActionStatus;

  protected readonly meta = computed(() => actionTypeMeta(this.action().type));
  protected readonly triggerIcon = computed(
    () => ACTION_TRIGGER_ICON[this.action().source.trigger],
  );

  protected readonly client = computed(() => {
    const client = this.action().client;
    return client?.id && client.id !== UNKNOWN_CLIENT_ID && client.name
      ? client
      : null;
  });

  protected readonly payload = computed(
    () => this.draft() ?? (this.action().payload as ActionPayload),
  );
  protected readonly body = computed(() => actionCardBody(this.action().type));

  protected readonly bodyInputs = computed(() => ({
    payload: this.payload(),
    editable: this.action().editable,
  }));

  private readonly rows = computed(() => {
    const payload = this.payload() as {
      items?: readonly DecidableItem[];
    } | null;

    return payload?.items ?? [];
  });

  protected readonly explicitDecisions = computed(
    () => this.meta().explicitDecisions === true,
  );

  protected readonly accepted = computed(
    () =>
      this.rows().filter((item) =>
        this.explicitDecisions()
          ? item.decision === ItemDecision.Accept
          : item.decision !== ItemDecision.Reject,
      ).length,
  );

  protected readonly undecided = computed(
    () => this.rows().filter((item) => item.decision === undefined).length,
  );

  protected readonly countsRows = computed(
    () => this.meta().verbCountKey != null,
  );

  protected readonly edited = computed(() => this.draft() !== null);

  protected readonly tileClass = computed(() =>
    this.meta().external
      ? 'bg-inserv-10 text-inserv-140'
      : 'bg-neutral-10 text-neutral-90',
  );

  protected readonly verbKey = computed(() => {
    const meta = this.meta();

    if (!meta.verbCountKey || !meta.verbOneKey) {
      return meta.verbKey;
    }

    return this.accepted() === 1 ? meta.verbOneKey : meta.verbCountKey;
  });

  protected readonly hasPayload = computed(() => this.payload() != null);

  protected readonly approveDisabled = computed(
    () =>
      !this.body() ||
      !this.hasPayload() ||
      (this.countsRows() && this.accepted() === 0),
  );

  protected acceptAll(): void {
    const payload = this.payload();

    if (payload) {
      this.onPayloadChange(withUndecidedAccepted(payload));
    }
  }

  protected readonly footerMode = computed<FooterMode>(() => {
    const status = this.action().status;
    if (status === ActionStatus.Executing) return 'executing';
    if (status !== ActionStatus.Pending) return 'settled';
    return this.rejecting() ? 'rejecting' : 'decision';
  });

  protected toggle(): void {
    const next = !this.isExpanded();
    this.localExpanded.set(next);
    this.expandedChange.emit(next);
  }

  onPayloadChange(payload: ActionPayload): void {
    this.draft.set(payload);
  }

  protected onApprove(): void {
    const payload =
      this.meta().acknowledgeOnly === true || this.explicitDecisions()
        ? this.payload()
        : withUndecidedAccepted(this.payload());

    this.approve.emit({ id: this.action().id, payload });
  }

  protected startReject(): void {
    this.rejecting.set(true);
  }

  protected cancelReject(): void {
    this.rejecting.set(false);
    this.rejectReason.set('');
  }

  protected confirmReject(): void {
    const reason = this.rejectReason().trim();
    this.reject.emit({ id: this.action().id, reason: reason || undefined });
    this.cancelReject();
  }
}

function withUndecidedAccepted(payload: ActionPayload): ActionPayload {
  const rows = (payload as { items?: readonly DecidableItem[] }).items;

  if (!rows) {
    return payload;
  }

  return {
    ...payload,
    items: rows.map((item) =>
      item.decision === undefined
        ? { ...item, decision: ItemDecision.Accept }
        : item,
    ),
  } as unknown as ActionPayload;
}
